import React from 'react';
import { Sparkles, QrCode, Send, TrendingUp, Map, Gift, RefreshCw, Coins, Trophy, Inbox, ListChecks, Compass, ScanLine, KeyRound } from 'lucide-react';

// Slides shown to a new guest the first time they land on /guest/home.
export const guestWelcomeTourSlides = [
  {
    key: 'guest-discover',
    icon: <Map size={48} strokeWidth={1.6} />,
    title: "Discover the area's hidden gems",
    body:
      'Hand-picked venues, local favorites, and live events curated for guests who want to truly experience where they stay.',
  },
  {
    key: 'guest-scan',
    icon: <QrCode size={48} strokeWidth={1.6} />,
    title: 'Your private guest pass',
    body:
      'Use your Allure QR at partner venues to unlock exclusive guest discounts and pay seamlessly through the app.',
  },
  {
    key: 'guest-rewards',
    icon: <Gift size={48} strokeWidth={1.6} />,
    title: 'The more you explore, the more you save',
    body:
      'As you spend through Allure, your access to higher guest discounts increases.',
  },
];

// Slides shown to a new host the first time they land on /host/dashboard.
export const hostWelcomeTourSlides = [
  {
    key: 'host-welcome',
    icon: <Sparkles size={48} strokeWidth={1.6} />,
    title: 'Elevate every guest experience',
    body:
      "As an Airbnb-Allure host, you invite your guests to the area's most worth-visiting venues and events, turning their stay into an experience they'll actually remember — while you earn more from every stay.",
  },
  {
    key: 'host-invite',
    icon: <Send size={48} strokeWidth={1.6} />,
    title: 'Invite your guests effortlessly',
    body:
      'Reservations sync automatically through Airbnb iCal. Confirm the guest, share their invite link, and let guests unlock Allure effortlessly — no extra work for you.',
  },
  {
    key: 'host-earn',
    icon: <TrendingUp size={48} strokeWidth={1.6} />,
    title: 'Earn while they explore',
    body:
      'Get a commission on every euro your guests spend at partner venues, receive optional guest tips for exceptional hospitality at checkout, and unlock monthly Host Pool rewards as your guests explore through Allure.',
  },
];


// Page-specific tour for /host/generate-qr (Invite Guest tab — left).
// Surfaces the two ways to create an invite (manual + Airbnb iCal sync) and
// how to share it.
export const hostInviteTourSlides = [
  {
    key: 'host-invite-page-overview',
    icon: <Send size={48} strokeWidth={1.6} />,
    title: 'Two ways to invite a guest',
    body:
      'Create a manual invite by picking the listing and dates, or let Airbnb iCal sync auto-detect every reservation so you can confirm with a single tap.',
  },
  {
    key: 'host-invite-page-sync',
    icon: <RefreshCw size={48} strokeWidth={1.6} />,
    title: 'Sync your Airbnb listings',
    body:
      "Connect each listing's iCal in Airbnb sync settings. Reservations appear automatically with check-in, checkout, and guest details — no manual entry needed.",
  },
  {
    key: 'host-invite-page-share',
    icon: <QrCode size={48} strokeWidth={1.6} />,
    title: 'Share the invite link instantly',
    body:
      'Once an invite is ready, copy the link or share it directly with your guest. They join Allure and unlock guest discounts before they even check in.',
  },
];

// Page-specific tour for /host/earnings (Earnings tab).
export const hostEarningsTourSlides = [
  {
    key: 'host-earnings-page-overview',
    icon: <Coins size={48} strokeWidth={1.6} />,
    title: 'Your earnings at a glance',
    body:
      "See your total earnings from guest spending at partner venues, plus the optional tips guests left you at checkout — all tracked in one place.",
  },
  {
    key: 'host-earnings-page-progress',
    icon: <TrendingUp size={48} strokeWidth={1.6} />,
    title: 'Track your monthly progress',
    body:
      'Your monthly progress bar shows how active your guests have been. The more they explore through Allure, the closer you get to the next reward tier.',
  },
  {
    key: 'host-earnings-page-pool',
    icon: <Trophy size={48} strokeWidth={1.6} />,
    title: 'Commission rate & Host Pool',
    body:
      'See your current commission rate on every euro guests spend, and your Host Pool progress — top performers each month share an extra bonus pool.',
  },
];

// Page-specific tour for /host/active-qr (Active Access — middle tab).
export const hostActiveAccessTourSlides = [
  {
    key: 'host-active-page-detected',
    icon: <Inbox size={48} strokeWidth={1.6} />,
    title: 'Auto-detected reservations',
    body:
      'When Airbnb iCal sync spots a new reservation, it lands here. Confirm the guest and share their invite link in a single tap — no setup required.',
  },
  {
    key: 'host-active-page-manual',
    icon: <ListChecks size={48} strokeWidth={1.6} />,
    title: 'Manual & accepted invites live here too',
    body:
      "Any invite you've created manually also shows up on this page. Once a guest accepts, you'll see them as Active (currently staying) or Upcoming (future arrival).",
  },
  {
    key: 'host-active-page-control',
    icon: <Compass size={48} strokeWidth={1.6} />,
    title: 'Your hosting control center',
    body:
      'Active Access is where you manage every stay — confirm new bookings, share invites, follow currently running guests, and prep for the ones arriving next.',
  },
];

// Page-specific tour for /guest/qr (Guest QR — left tab on guest nav).
// Order: 1) how the QR is unlocked (host invite), 2) how to use it at the
// venue, 3) how the discount scales with spend.
export const guestQrTourSlides = [
  {
    key: 'guest-qr-page-unlock',
    icon: <KeyRound size={48} strokeWidth={1.6} />,
    title: 'Your QR access is unlocked by your host',
    body:
      "Your QR pass appears once you accept your host's Allure invite. It activates on your check-in day and deactivates the day you check out — your private guest pass for the entire stay.",
  },
  {
    key: 'guest-qr-page-scan',
    icon: <ScanLine size={48} strokeWidth={1.6} />,
    title: 'Show your QR code before paying at any partner venue.',
    body:
      'Once the venue scans it, your guest discount is applied to the bill — no codes, no manual entry.',
  },
  {
    key: 'guest-qr-page-scale',
    icon: <TrendingUp size={48} strokeWidth={1.6} />,
    title: 'The more you spend, the more you save',
    body:
      'Your discount scales automatically as you spend through Allure. Every visit moves you toward a higher tier — bigger discounts, with zero extra effort from you.',
  },
];

